import React, { useState, useEffect } from 'react';

// Types
interface AuditLog {
    id: number;
    tenant_id: number;
    user_id?: number | null;
    username?: string | null;
    action_type: string;
    resource_type: string;
    resource_id?: string | null;
    details?: string | null;
    ip_address?: string | null;
    timestamp: string;
}

const ACTION_TYPES = [
    'INSERT', 'UPDATE', 'DELETE', 'EXECUTE', 'LOGIN', 'LOGOUT'
];

const AuditLogViewer: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Filters
    const [filterAction, setFilterAction] = useState<string>('');

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, filterAction]);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            if (!token) {
                throw new Error('認証トークンがありません');
            }

            let url = `/api/admin/audit-logs?skip=${page * rowsPerPage}&limit=${rowsPerPage}`;
            if (filterAction) {
                url += `&action_type=${filterAction}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch logs: ${response.statusText}`);
            }

            const data = await response.json();
            setLogs(data);
        } catch (err: any) {
            console.error('Error fetching audit logs:', err);
            setError(err.message || 'ログの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const formatDetails = (details: string | null | undefined) => {
        if (!details) return '-';
        try {
            if (typeof details === 'object') {
                return JSON.stringify(details);
            }
            return details;
        } catch (e) {
            return details;
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString('ja-JP');
        } catch (e) {
            return dateString;
        }
    };

    const getActionColor = (action: string) => {
        switch (action) {
            case 'INSERT': return '#e8f5e9'; // Green-ish
            case 'UPDATE': return '#e3f2fd'; // Blue-ish
            case 'DELETE': return '#ffebee'; // Red-ish
            case 'EXECUTE': return '#fff3e0'; // Orange-ish
            default: return 'transparent';
        }
    };

    return (
        <div style={{ padding: '20px', fontFamily: '"MS UI Gothic", sans-serif' }}>
            <h2 style={{
                borderBottom: '2px solid #000080',
                paddingBottom: '5px',
                marginBottom: '20px',
                color: '#000080',
                fontSize: '18px'
            }}>
                システム監査ログ
            </h2>

            <div style={{
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc'
            }}>
                <label style={{ marginRight: '10px', fontSize: '14px' }}>
                    アクションタイプ:
                    <select
                        value={filterAction}
                        onChange={(e) => setFilterAction(e.target.value)}
                        style={{ marginLeft: '5px', padding: '2px' }}
                    >
                        <option value="">全て</option>
                        {ACTION_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </label>

                <button
                    onClick={() => fetchLogs()}
                    style={{ marginLeft: '10px', padding: '2px 8px', cursor: 'pointer' }}
                >
                    再読み込み
                </button>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#ffdddd',
                    color: 'red',
                    padding: '10px',
                    marginBottom: '20px',
                    border: '1px solid red'
                }}>
                    {error}
                </div>
            )}

            {loading && <div style={{ textAlign: 'center', padding: '20px' }}>読み込み中...</div>}

            {!loading && (
                <>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '12px',
                        backgroundColor: '#fff',
                        border: '1px solid #999'
                    }}>
                        <thead style={{ backgroundColor: '#e0e0e0' }}>
                            <tr>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>日時</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>ユーザー</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>アクション</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>リソース</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>ID</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>詳細</th>
                                <th style={{ border: '1px solid #999', padding: '4px' }}>IPアドレス</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ border: '1px solid #999', padding: '10px', textAlign: 'center' }}>
                                        ログがありません
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} style={{ backgroundColor: getActionColor(log.action_type) }}>
                                        <td style={{ border: '1px solid #999', padding: '4px' }}>{formatDate(log.timestamp)}</td>
                                        <td style={{ border: '1px solid #999', padding: '4px' }}>
                                            {log.username || (log.user_id ? `User:${log.user_id}` : '-')}
                                        </td>
                                        <td style={{ border: '1px solid #999', padding: '4px', fontWeight: 'bold' }}>{log.action_type}</td>
                                        <td style={{ border: '1px solid #999', padding: '4px' }}>{log.resource_type}</td>
                                        <td style={{ border: '1px solid #999', padding: '4px' }}>{log.resource_id || '-'}</td>
                                        <td style={{
                                            border: '1px solid #999',
                                            padding: '4px',
                                            maxWidth: '300px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }} title={String(log.details)}>
                                            {formatDetails(log.details)}
                                        </td>
                                        <td style={{ border: '1px solid #999', padding: '4px' }}>{log.ip_address || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontSize: '12px' }}>
                        <button
                            disabled={page === 0}
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            style={{ marginRight: '5px', padding: '2px 8px', cursor: page === 0 ? 'not-allowed' : 'pointer' }}
                        >
                            前のページ
                        </button>
                        <span style={{ margin: '0 10px' }}>ページ {page + 1}</span>
                        <button
                            disabled={logs.length < rowsPerPage}
                            onClick={() => setPage(p => p + 1)}
                            style={{ marginLeft: '5px', padding: '2px 8px', cursor: logs.length < rowsPerPage ? 'not-allowed' : 'pointer' }}
                        >
                            次のページ
                        </button>

                        <select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setPage(0);
                            }}
                            style={{ marginLeft: '10px' }}
                        >
                            <option value={20}>20件</option>
                            <option value={50}>50件</option>
                            <option value={100}>100件</option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );
};

export default AuditLogViewer;
